import React, { useState, useEffect } from "react";
import axios from "axios";
import DatePicker from "react-datepicker";
import { toast} from "react-toastify";
import "react-datepicker/dist/react-datepicker.css";
import DynamicSelect from "../DynamicSelect";

const API_URL = "https://api.muktihospital.com/api/birth-records";
const API_KEY = "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";

const RequiredIcon = () => <span className="text-red-500 ml-1">*</span>;

const BirthRecordForm = ({ onSuccess, initialData, onCancel }) => {
  const [formData, setFormData] = useState({
    childName: "",
    gender: null,
    weight: "",
    birthDate: new Date(),
    phone: "",
    address: "",
    motherName: "",
    fatherName: "",
    report: "",
  });

  const [files, setFiles] = useState({
    childPhoto: null,
    motherPhoto: null,
    fatherPhoto: null,
    documentPhoto: null,
  });

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (initialData) {
      setFormData({
        childName: initialData.childName || "",
        gender: initialData.gender
          ? { value: initialData.gender, label: initialData.gender }
          : null,
        weight: initialData.weight || "",
        birthDate: initialData.birthDate ? new Date(initialData.birthDate) : new Date(),
        phone: initialData.phone || "",
        address: initialData.address || "",
        motherName: initialData.motherName || "",
        fatherName: initialData.fatherName || "",
        report: initialData.report || "",
      });
    }
  }, [initialData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (selectedOption) => {
    setFormData((prev) => ({ ...prev, gender: selectedOption }));
  };

  const handleFileChange = (e) => {
    setFiles((prev) => ({ ...prev, [e.target.name]: e.target.files[0] }));
  };

  const resetForm = () => {
    setFormData({
      childName: "",
      gender: null,
      weight: "",
      birthDate: new Date(),
      phone: "",
      address: "",
      motherName: "",
      fatherName: "",
      report: "",
    });
    setFiles({
      childPhoto: null,
      motherPhoto: null,
      fatherPhoto: null,
      documentPhoto: null,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Validate required fields
    if (
      !formData.childName ||
      !formData.gender ||
      !formData.weight ||
      !formData.birthDate ||
      !formData.motherName
    ) {
      toast.error("Please fill in all required fields.");
      setLoading(false);
      return;
    }

    const data = new FormData();
    Object.entries(formData).forEach(([key, value]) => {
      if (key === "birthDate") {
        data.append("birthDate", new Date(value).toISOString());
      } else if (key === "gender") {
        data.append("gender", value?.value || "");
      } else {
        data.append(key, value);
      }
    });

    Object.entries(files).forEach(([key, file]) => {
      if (file) {
        data.append(key, file);
      }
    });

    try {
      const url = initialData ? `${API_URL}/${initialData.id}` : API_URL;
      const method = initialData ? "put" : "post";

      await axios[method](url, data, {
        headers: {
          "Content-Type": "multipart/form-data",
          "x-api-key": API_KEY,
        },
      });
      onSuccess?.();
    } catch (error) {
      console.error("Error saving birth record:", error);
      toast.error(`Failed to ${initialData ? "update" : "add"} birth record.`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 px-4 sm:px-0 print-hidden"
    >
      <div className="bg-white rounded-lg shadow-lg w-full max-w-[95%] sm:max-w-[80%] md:max-w-[700px] lg:max-w-[800px] max-h-[90vh] overflow-y-auto">
        <div className="p-4 sm:p-6 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-lg sm:text-xl font-bold text-gray-700">
            {initialData ? "Edit Birth Record" : "Add Birth Record"}
          </h2>
          <button
            type="button"
            className="text-gray-500 hover:text-gray-800 text-lg sm:text-xl"
            onClick={() => {
              resetForm();
              onCancel?.();
            }}
          >
            ✕
          </button>
        </div>

        <div className="p-4 sm:p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Child Name <RequiredIcon />
            </label>
            <input
              type="text"
              name="childName"
              className="w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-gray-700"
              value={formData.childName}
              onChange={handleChange}
              required
              placeholder="Enter child's name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Gender <RequiredIcon />
            </label>
            <DynamicSelect
              options={[
                { value: "Male", label: "Male" },
                { value: "Female", label: "Female" },
              ]}
              value={formData.gender}
              onChange={handleSelectChange}
              placeholder="Select Gender"
              isSearchable={true}
              isClearable
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Weight (kg) <RequiredIcon />
            </label>
            <input
              type="number"
              name="weight"
              className="w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-gray-700"
              value={formData.weight}
              onChange={handleChange}
              required
              placeholder="e.g. 3.2"
              step="0.1"
              min="0"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Birth Date <RequiredIcon />
            </label>
            <DatePicker
              selected={formData.birthDate}
              onChange={(date) => setFormData({ ...formData, birthDate: date })}
              className="w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-gray-700"
              required
              dateFormat="MM/dd/yyyy"
              maxDate={new Date()}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Mother Name <RequiredIcon />
            </label>
            <input
              type="text"
              name="motherName"
              className="w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-gray-700"
              value={formData.motherName}
              onChange={handleChange}
              required
              placeholder="Enter mother's name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Father Name
            </label>
            <input
              type="text"
              name="fatherName"
              className="w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-gray-700"
              value={formData.fatherName}
              onChange={handleChange}
              placeholder="Enter father's name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone
            </label>
            <input
              type="text"
              name="phone"
              className="w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-gray-700"
              value={formData.phone}
              onChange={handleChange}
              placeholder="Enter phone number"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Report
            </label>
            <input
              type="text"
              name="report"
              className="w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-gray-700"
              value={formData.report}
              onChange={handleChange}
              placeholder="Enter report details"
            />
          </div>

          <div className="sm:col-span-2 lg:col-span-3">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Address
            </label>
            <textarea
              name="address"
              className="w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-gray-700"
              value={formData.address}
              onChange={handleChange}
              placeholder="Enter address"
              rows={4}
            />
          </div>
        </div>

        <div className="p-4 sm:pt-0 sm:p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            ["childPhoto", "Child Photo"],
            ["motherPhoto", "Mother Photo"],
            ["fatherPhoto", "Father Photo"],
            ["documentPhoto", "Document Photo"],
          ].map(([name, label]) => (
            <div key={name}>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {label}
              </label>
              <input
                type="file"
                name={name}
                className="w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-gray-700"
                onChange={handleFileChange}
                accept="image/*"
              />
              {initialData?.[name] && !files[name] && (
                <img
                  src={`https://api.muktihospital.com/uploads/${initialData[name]}`}
                  alt={label}
                  className="w-20 h-20 object-cover mt-2 rounded border"
                  onError={(e) => {
                    e.target.src = "/path/to/placeholder-image.jpg"; // Replace with actual placeholder
                  }}
                />
              )}
              {files[name] && (
                <img
                  src={URL.createObjectURL(files[name])}
                  alt={label}
                  className="w-20 h-20 object-cover mt-2 rounded border"
                />
              )}
            </div>
          ))}
        </div>

        <div className="flex flex-col sm:flex-row justify-end gap-3 p-4 sm:p-6 border-t border-gray-200">
          <button
            type="button"
            className="bg-[#E7633D]/90 text-sm text-white py-2 px-6 rounded-md hover:bg-[#E7633D] transition-all duration-300 w-full sm:w-auto"
            onClick={() => {
              resetForm();
              onCancel?.();
            }}
            disabled={loading}
          >
            {initialData ? "Cancel" : "Clear"}
          </button>
          <button
            type="submit"
            className="bg-M-primary-color/90 text-sm text-white py-2 px-6 rounded-md hover:bg-M-primary-color transition-all duration-200 w-full sm:w-auto"
            disabled={loading}
          >
            {loading ? "Saving..." : initialData ? "Update" : "Save"}
          </button>
        </div>
      </div>
    </form>
  );
};

export default BirthRecordForm;